import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/Layout/AppLayout';
import DeadlineBanner from '../components/DeadlineBanner';
import { api } from '../services/api';
import type { ApplicationFormResponse, DeficiencyRequest, DeadlinesConfig } from '@vedjegy/shared';

function sectionCharCount(data: Record<string, Record<string, string>>, sectionId: string): number {
  const key = sectionId.startsWith('3.') ? sectionId.split('_')[0] : sectionId;
  const fields = data[key] ?? {};
  return Object.values(fields).reduce((sum, v) => sum + (v?.length ?? 0), 0);
}

type SectionStatus = 'done' | 'in_progress' | 'not_started';

function sectionStatus(chars: number, min: number | null): SectionStatus {
  if (!min) return chars > 0 ? 'done' : 'not_started';
  if (chars >= min) return 'done';
  if (chars > 0)    return 'in_progress';
  return 'not_started';
}

const STATUS_LABEL: Record<SectionStatus, string> = {
  done:        '✓ Kész',
  in_progress: '● Folyamatban',
  not_started: '○ Nem kezdett',
};
const STATUS_COLOR: Record<SectionStatus, string> = {
  done:        '#1a7a40',
  in_progress: '#b07d00',
  not_started: 'var(--muted-foreground)',
};
const STATUS_BG: Record<SectionStatus, string> = {
  done:        'rgba(78,192,124,.12)',
  in_progress: 'rgba(234,219,135,.3)',
  not_started: 'var(--muted)',
};

export default function ApplicationDashboardPage() {
  const navigate = useNavigate();
  const [form, setForm]       = useState<ApplicationFormResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const [deficiency, setDeficiency]                     = useState<DeficiencyRequest | null>(null);
  const [deficiencyResponse, setDeficiencyResponse]     = useState('');
  // Per-section responses keyed by section ID
  const [sectionResponses, setSectionResponses]         = useState<Record<string, string>>({});
  const [deficiencySubmitting, setDeficiencySubmitting] = useState(false);
  const [deficiencyError, setDeficiencyError]           = useState('');
  const [deficiencySubmitted, setDeficiencySubmitted]   = useState(false);
  const [deadlines, setDeadlines]                       = useState<DeadlinesConfig | null>(null);

  useEffect(() => {
    Promise.all([
      api.applicationForm.load(),
      api.applicationForm.getDeficiency().catch(() => ({ deficiency: null })),
      api.config.getDeadlines().catch(() => null),
    ])
      .then(([formRes, defRes, deadlineRes]) => {
        setForm(formRes);
        setDeficiency(defRes.deficiency);
        setDeadlines(deadlineRes);
      })
      .catch(() => setError('Nem sikerült betölteni a pályázatot.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleDeficiencySubmit() {
    setDeficiencySubmitting(true);
    setDeficiencyError('');
    try {
      const filteredSectionResponses = Object.fromEntries(
        Object.entries(sectionResponses).filter(([, v]) => v.trim().length > 0),
      );
      await api.applicationForm.deficiencySubmit({
        response: deficiencyResponse.trim() || undefined,
        sectionResponses: Object.keys(filteredSectionResponses).length > 0 ? filteredSectionResponses : undefined,
      });
      setDeficiencySubmitted(true);
      const updated = await api.applicationForm.load();
      setForm(updated);
    } catch (e) {
      setDeficiencyError(e instanceof Error ? e.message : 'Küldési hiba.');
    } finally {
      setDeficiencySubmitting(false);
    }
  }

  if (loading) {
    return (
      <AppLayout>
        <div style={{ padding: 40, color: 'var(--muted-foreground)' }}>Betöltés...</div>
      </AppLayout>
    );
  }

  if (error || !form) {
    return (
      <AppLayout>
        <div style={{ padding: 40 }}>
          <div className="alert alert-destructive"><span>⚠</span><div><p>{error || 'Pályázat nem található.'}</p></div></div>
        </div>
      </AppLayout>
    );
  }

  if (form.status === 'deficiency_submitted' || deficiencySubmitted) {
    return (
      <AppLayout appStatus={deficiencySubmitted ? 'deficiency_submitted' : form.status}>
        <div style={{ padding: '48px 40px', maxWidth: 2000 }}>
          <div style={{ background: 'rgba(78,192,124,.12)', border: '1px solid #4EC07C', borderRadius: 'var(--radius-lg)', padding: '32px 36px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
            <h2 style={{ margin: '0 0 8px', color: 'var(--primary)', fontSize: 20 }}>Hiánypótlás beküldve</h2>
            <p style={{ color: 'var(--muted-foreground)', margin: 0, fontSize: 14 }}>
              A kiegészítő információt megkaptuk. A bíráló folytatja az értékelést.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (form.status === 'pending_exclusion_review') {
    return (
      <AppLayout appStatus="pending_exclusion_review">
        <div style={{ padding: '48px 40px', maxWidth: 2000 }}>
          <div style={{
            background: '#fffbeb',
            border: '2px solid #f59e0b',
            borderRadius: 'var(--radius-lg)',
            padding: '32px 36px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
            <h2 style={{ margin: '0 0 8px', color: '#92400e', fontSize: 20 }}>Adminisztrátori vizsgálatra vár</h2>
            <p style={{ color: '#78350f', margin: '0 0 16px', fontSize: 14, lineHeight: 1.6 }}>
              A pályázat beküldésekor kizáró tényező aktiválódott, amelyet az adminisztrátor megvizsgál.
              A vizsgálat lezárultáig a pályázat nem szerkeszthető.
            </p>
            <p style={{ color: 'var(--muted-foreground)', margin: 0, fontSize: 13 }}>
              Amennyiben kérdése van, kérjük, lépjen kapcsolatba a Kamarával.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (form.status === 'excluded') {
    return (
      <AppLayout appStatus="excluded">
        <div style={{ padding: '48px 40px', maxWidth: 2000 }}>
          <div style={{
            background: '#fef2f2',
            border: '2px solid #fca5a5',
            borderRadius: 'var(--radius-lg)',
            padding: '32px 36px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✗</div>
            <h2 style={{ margin: '0 0 8px', color: '#b91c1c', fontSize: 20 }}>Pályázat kizárva</h2>
            <p style={{ color: '#7f1d1d', margin: '0 0 16px', fontSize: 14, lineHeight: 1.6 }}>
              Az adminisztrátori vizsgálat alapján pályázatát kizárásra kerültek.
              Az értesítőben szereplő indoklást e-mailben is megkapta.
            </p>
            <p style={{ color: 'var(--muted-foreground)', margin: 0, fontSize: 13 }}>
              Jogorvoslati lehetőségekért, illetve további kérdéseiért kérjük, lépjen kapcsolatba a Kamarával.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (form.status === 'submitted') {
    return (
      <AppLayout appStatus="submitted">
        <div style={{ padding: '48px 40px', maxWidth: 2000 }}>
          <div style={{ background: 'rgba(78,192,124,.12)', border: '1px solid #4EC07C', borderRadius: 'var(--radius-lg)', padding: '32px 36px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
            <h2 style={{ margin: '0 0 8px', color: 'var(--primary)', fontSize: 20 }}>Pályázat beküldve</h2>
            <p style={{ color: 'var(--muted-foreground)', margin: 0, fontSize: 14 }}>
              A pályázatot megkaptuk. Hamarosan felvesszük Önnel a kapcsolatot a következő lépésekről.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const sections = form.sectionConfig.map((cfg) => {
    const chars  = sectionCharCount(form.data, cfg.sectionId);
    const status = sectionStatus(chars, cfg.minChars ?? null);
    return { cfg, chars, status };
  });

  const done  = sections.filter((s) => s.status === 'done').length;
  const total = sections.length;
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0;

  const totalChars = (() => {
    const seen = new Set<string>();
    let sum = 0;
    for (const { cfg } of sections) {
      const key = cfg.sectionId.startsWith('3.') ? cfg.sectionId.split('_')[0] : cfg.sectionId;
      if (!seen.has(key)) {
        seen.add(key);
        sum += Object.values(form.data[key] ?? {}).reduce((s, v) => s + (v?.length ?? 0), 0);
      }
    }
    return sum;
  })();

  const hasPendingDeficiency = form.status === 'deficiency_required' && deficiency && !deficiency.resolvedAt;
  // These statuses lock the form — editing is no longer allowed
  const isReadOnly = ['under_review', 'approved', 'rejected'].includes(form.status);

  return (
    <AppLayout appStatus={form.status}>
      <div style={{ padding: '32px 40px', maxWidth: 2000 }}>

        {/* Deadline countdown banner */}
        {deadlines && <DeadlineBanner type="application" deadlines={deadlines} />}

        {/* Hiánypótlás banner */}
        {hasPendingDeficiency && deficiency && (
          <div style={{
            background: 'rgba(240,192,64,.15)',
            border: '2px solid #f0c040',
            borderRadius: 'var(--radius-lg)',
            padding: '20px 24px',
            marginBottom: 28,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: '#7a5a00', marginBottom: 10 }}>
              Hiánypótlási kérelem
            </div>
            <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {deficiency.description}
            </p>
            {deficiency.deadline && (
              <div style={{ fontSize: 12, color: '#7a5a00', fontWeight: 600, marginBottom: 16 }}>
                Határidő: {new Date(deficiency.deadline).toLocaleDateString('hu-HU')}
              </div>
            )}

            {/* Per-section notes from reviewer + response textareas */}
            {deficiency.sectionNotes && Object.keys(deficiency.sectionNotes).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#7a5a00', marginBottom: 10 }}>
                  Kérdésenkénti megjegyzések a bírálótól:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {Object.entries(deficiency.sectionNotes).map(([sectionId, note]) => (
                    <div key={sectionId} style={{ background: 'rgba(255,255,255,.7)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(240,192,64,.5)', padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#7a5a00', marginBottom: 4 }}>{sectionId}</div>
                      <div style={{ fontSize: 13, color: 'var(--primary)', whiteSpace: 'pre-wrap', lineHeight: 1.5, marginBottom: 10 }}>{note}</div>
                      <textarea spellCheck={true} lang="hu"
                        value={sectionResponses[sectionId] ?? ''}
                        onChange={(e) => setSectionResponses((prev) => ({ ...prev, [sectionId]: e.target.value }))}
                        placeholder="Válasz erre a megjegyzésre (nem kötelező)..."
                        rows={2}
                        style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', fontSize: 13 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Global response textarea */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--secondary-foreground)', marginBottom: 6 }}>
                Általános megjegyzés a bírálónak (nem kötelező)
              </label>
              <textarea spellCheck={true} lang="hu"
                value={deficiencyResponse}
                onChange={(e) => setDeficiencyResponse(e.target.value)}
                placeholder="Írja le röviden, mit egészített ki vagy változtatott a pályázatban..."
                rows={3}
                style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box', fontSize: 13 }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className="btn btn-primary"
                disabled={deficiencySubmitting}
                onClick={() => void handleDeficiencySubmit()}
              >
                {deficiencySubmitting ? 'Küldés...' : 'Hiánypótlás beküldése'}
              </button>
              {deficiencyError && (
                <div style={{ fontSize: 12, color: 'var(--destructive)' }}>{deficiencyError}</div>
              )}
            </div>
          </div>
        )}

        {/* Előminősítés teljesítve banner */}
        {form.status === 'preq_passed' && (
          <div style={{
            background: 'rgba(78,192,124,.12)',
            border: '1px solid #4EC07C',
            borderRadius: 'var(--radius-lg)',
            padding: '16px 24px',
            marginBottom: 28,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}>
            <span style={{ fontSize: 28, lineHeight: 1, color: '#1a7a40' }}>✓</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#064935' }}>Előminősítés sikeresen teljesítve</div>
              <div style={{ fontSize: 13, color: '#1a7a40', marginTop: 3 }}>Megkezdheti a pályázati adatlap kitöltését az alábbi fejezetek segítségével.</div>
            </div>
          </div>
        )}

        {/* Status banners for review / decision states */}
        {form.status === 'under_review' && (
          <div style={{
            background: '#f5f3ff', border: '1px solid #c4b5fd',
            borderRadius: 'var(--radius-lg)', padding: '16px 24px',
            marginBottom: 28, display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>🔍</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#4c1d95' }}>Bírálat alatt</div>
              <div style={{ fontSize: 13, color: '#6d28d9', marginTop: 3 }}>A pályázatot jelenleg bírálják. Ez idő alatt az adatlap nem szerkeszthető.</div>
            </div>
          </div>
        )}
        {form.status === 'approved' && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #86efac',
            borderRadius: 'var(--radius-lg)', padding: '16px 24px',
            marginBottom: 28, display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>🏆</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#14532d' }}>Pályázat elfogadva</div>
              <div style={{ fontSize: 13, color: '#166534', marginTop: 3 }}>Gratulálunk! A pályázatot elbírálták és elfogadták.</div>
            </div>
          </div>
        )}
        {form.status === 'rejected' && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5',
            borderRadius: 'var(--radius-lg)', padding: '16px 24px',
            marginBottom: 28, display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <span style={{ fontSize: 22, lineHeight: 1 }}>✗</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#7f1d1d' }}>Pályázat elutasítva</div>
              <div style={{ fontSize: 13, color: '#991b1b', marginTop: 3 }}>A pályázatot elutasították. Kérdés esetén vegye fel a kapcsolatot az irodával.</div>
            </div>
          </div>
        )}

        {/* Fejléc */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
              PÁLYÁZATI ADATLAP · {new Date().getFullYear()} · {form.formType === 'sole_trader' ? 'Egyéni vállalkozás' : 'Társas vállalkozás'}
            </div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>Pályázati adatlap</h1>
          </div>
          {!isReadOnly && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/application/form')}
              style={{ flexShrink: 0, marginTop: 4 }}
            >
              Adatlap szerkesztése →
            </button>
          )}
        </div>

        {/* Haladás */}
        <div style={{
          background: '#fff', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)', padding: '18px 24px',
          marginBottom: 24, boxShadow: 'var(--shadow-sm)',
          display: 'flex', alignItems: 'center', gap: 32,
        }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', lineHeight: 1.1 }}>{done} / {total}</div>
            <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 2 }}>Fejezet kész</div>
          </div>
          <div style={{ width: 1, height: 36, background: 'var(--border)' }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary)', lineHeight: 1.1 }}>{totalChars.toLocaleString('hu-HU')}</div>
            <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 2 }}>Karakter összesen</div>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ minWidth: 180 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: 'var(--primary)', marginBottom: 6 }}>
              <span>Haladás</span><span>{pct}%</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: pct + '%' }} />
            </div>
          </div>
        </div>

        {/* Fejezetek */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sections.map(({ cfg, chars, status }) => (
            <div
              key={cfg.sectionId}
              onClick={() => !isReadOnly && navigate('/application/form?section=' + cfg.sectionId)}
              style={{
                background: '#fff', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)', padding: '14px 20px',
                boxShadow: 'var(--shadow-sm)', cursor: isReadOnly ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 16,
                transition: 'border-color .15s',
              }}
              onMouseEnter={(e) => { if (!isReadOnly) e.currentTarget.style.borderColor = 'var(--primary)'; }}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            >
              <div style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: STATUS_COLOR[status] }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {cfg.title}
                </div>
                {cfg.minChars && chars > 0 && chars < cfg.minChars && (
                  <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 80, height: 3, background: '#e8e2c0', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: Math.min(100, Math.round((chars / cfg.minChars) * 100)) + '%', background: '#c9a800', borderRadius: 999 }} />
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{chars} / {cfg.minChars} kar.</span>
                  </div>
                )}
              </div>
              {chars > 0 && (
                <div style={{ fontSize: 12, color: 'var(--muted-foreground)', flexShrink: 0 }}>
                  {chars.toLocaleString('hu-HU')} kar.
                </div>
              )}
              {cfg.maxScore > 0 && (
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)', flexShrink: 0, width: 48, textAlign: 'right' }}>
                  max {cfg.maxScore} pt
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, flexShrink: 0, background: STATUS_BG[status], color: STATUS_COLOR[status] }}>
                {STATUS_LABEL[status]}
              </div>
              <div style={{ color: 'var(--muted-foreground)', fontSize: 14, flexShrink: 0 }}>›</div>
            </div>
          ))}
        </div>

      </div>
    </AppLayout>
  );
}
